import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { HelpCircle, Phone, Mail } from 'lucide-react';

const faqCategories = [
  {
    title: 'About Our Kittens',
    icon: '🐱',
    questions: [
      {
        question: 'What breeds do you specialize in?',
        answer:
          'We specialize in Scottish Fold and Scottish Straight kittens. Both breeds are known for their sweet temperaments, round faces, and affectionate personalities. Scottish Folds have distinctive folded ears, while Scottish Straights have normal upright ears.',
      },
      {
        question: 'How old are the kittens when they go to their new homes?',
        answer:
          'Our kittens go to their new homes at 12-14 weeks of age. This ensures they are fully weaned, litter trained, and have received their initial vaccinations. This age also allows them to develop proper social skills with their mother and siblings.',
      },
      {
        question: 'Are your kittens socialized?',
        answer:
          'Yes! All our kittens are raised in our home and are handled daily from birth. They are exposed to various sounds, people, and experiences to ensure they grow up to be confident, well-adjusted cats.',
      },
      {
        question: 'What colors and patterns are available?',
        answer:
          'Our kittens come in a variety of colors including white, cream, silver, golden, blue, and various tabby patterns. Check our Available Kittens page for current availability.',
      },
    ],
  },
  {
    title: 'Adoption Process',
    icon: '📋',
    questions: [
      {
        question: 'How do I reserve a kitten?',
        answer:
          'No deposit is required to reserve your kitten. Simply contact us to express your interest, choose the kitten you love, and payment is made only when you pick up your kitten. This keeps the reservation process simple and transparent.',
      },
      {
        question: 'What is included with my kitten?',
        answer:
          'Each kitten comes with: age-appropriate vaccinations, deworming treatments, a health certificate from our veterinarian, a kitten care guide, a small bag of their current food, a favorite toy, and our lifetime support.',
      },
      {
        question: 'Do you ship kittens?',
        answer:
          'We offer both pickup and FREE delivery options. Pickup is available from our location in Covington Township, Pennsylvania. We also provide complimentary delivery to your location — we never ship kittens as cargo.',
      },
      {
        question: 'Can I visit and meet the kittens before adopting?',
        answer:
          'Yes! We welcome visits by appointment only at our Covington Township, PA location. This allows us to ensure a calm environment for our cats and kittens. Contact us to schedule a visit.',
      },
    ],
  },
  {
    title: 'Health & Care',
    icon: '🏥',
    questions: [
      {
        question: 'Are the kittens vaccinated?',
        answer:
          'Yes, all kittens receive age-appropriate vaccinations including FVRCP (feline distemper combo). We provide a complete vaccination record and schedule for future boosters.',
      },
      {
        question: 'What food do you recommend?',
        answer:
          'We feed our kittens high-quality, grain-free kitten food. When you take your kitten home, we\'ll provide a sample of their current food and recommendations for continued nutrition.',
      },
    ],
  },
  {
    title: 'Pricing & Payment',
    icon: '💰',
    questions: [
      {
        question: 'How much do your kittens cost?',
        answer:
          'Kitten prices vary based on breed, color, pattern, and ear type (fold vs straight). Please visit our Available Kittens page for current pricing or contact us for more information.',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'We accept cash, bank transfers, Zelle, Venmo, and PayPal. No deposit is required — full payment is made when you pick up your kitten.',
      },
      {
        question: 'Do I need to pay anything upfront?',
        answer:
          'No! We do not require any upfront payment or deposit. You pay only when you pick up your kitten. This gives you peace of mind and keeps the process simple and transparent.',
      },
      {
        question: 'Are there any additional costs?',
        answer:
          'The listed price includes the kitten, vaccinations, health certificate, starter kit, and FREE delivery! We recommend budgeting for initial supplies like a carrier, litter box, and scratching post.',
      },
    ],
  },
];

export default function FAQ() {
  return (
    <main className="min-h-screen bg-[#FAF8F5]">
      <SEO
        title="FAQ | KittenState Scottish Fold & Scottish Straight Kittens"
        description="Find answers to common questions about adopting Scottish Fold and Scottish Straight kittens from KittenState. Learn about kitten care, delivery, visits, and our adoption process."
        keywords="Scottish Fold FAQ, Scottish Straight questions, KittenState FAQ, kitten adoption questions, kitten care"
        url="https://kittenstate.com/faq"
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-rose-50 to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-3">
            Common Questions
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-stone-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-base lg:text-lg text-stone-600 max-w-2xl mx-auto">
            Everything you need to know about our kittens and the adoption process.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category, categoryIndex) => (
            <div
              key={`faq-category-${categoryIndex}`}
              className="mb-10"
              data-testid={`faq-category-${categoryIndex}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-[#E85D75]" />
                </div>
                <h2 className="text-xl font-serif font-semibold text-stone-800">
                  {category.title}
                </h2>
              </div>
              <div className="bg-white rounded-2xl border border-rose-100/50 shadow-sm overflow-hidden">
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((faq, faqIndex) => (
                    <AccordionItem
                      key={`faq-${categoryIndex}-${faqIndex}`}
                      value={`item-${categoryIndex}-${faqIndex}`}
                      className="border-rose-100 px-6"
                    >
                      <AccordionTrigger
                        className="text-left text-stone-800 hover:text-[#E85D75] hover:no-underline py-5 text-base font-medium"
                        data-testid={`faq-trigger-${categoryIndex}-${faqIndex}`}
                      >
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-stone-600 leading-relaxed pb-5 text-sm">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          ))}

          {/* Still Have Questions */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8 text-center mt-12 border border-rose-100">
            <h3 className="text-2xl font-serif font-semibold text-stone-800 mb-4">
              Still Have Questions?
            </h3>
            <p className="text-stone-600 mb-6 max-w-lg mx-auto">
              Can't find the answer you're looking for? Our team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  className="bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 rounded-full px-6 py-5"
                  data-testid="faq-contact-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
              <a href="tel:929-333-1936">
                <Button
                  variant="outline"
                  className="rounded-full px-6 py-5 border-[#E85D75] text-[#E85D75] hover:bg-rose-50"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call 929-333-1936
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
